import React, { useEffect, useState } from 'react';
import { XIcon, CreditCardIcon, WalletIcon, SmartphoneIcon, CheckCircleIcon } from 'lucide-react';
interface AddFundsBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (amount: number, paymentMethod: string) => void;
}
export function AddFundsBottomSheet({
  isOpen,
  onClose,
  onConfirm
}: AddFundsBottomSheetProps) {
  const [amount, setAmount] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const quickAmounts = [50, 100, 200, 500];
  const paymentMethods = [{
    id: 'card',
    name: 'Credit/Debit Card',
    icon: <CreditCardIcon className="w-5 h-5" data-id="element-126" data-mp="g3gd77jpvccn" />,
    description: 'Visa, Mastercard'
  }, {
    id: 'mada',
    name: 'Mada',
    icon: <CreditCardIcon className="w-5 h-5" data-id="element-127" data-mp="q0b7fe8yiql4" />,
    description: 'Saudi Payment Network'
  }, {
    id: 'apple-pay',
    name: 'Apple Pay',
    icon: <WalletIcon className="w-5 h-5" data-id="element-128" data-mp="v07ddj17ss3b" />,
    description: 'Fast & Secure'
  }, {
    id: 'stc-pay',
    name: 'STC Pay',
    icon: <SmartphoneIcon className="w-5 h-5" data-id="element-129" data-mp="30cbdl591f2vc" />,
    description: 'Mobile Wallet'
  }];
  const handleConfirm = () => {
    if (amount && selectedPaymentMethod) {
      onConfirm(parseFloat(amount), selectedPaymentMethod);
      setAmount('');
      setSelectedPaymentMethod('');
    }
  };
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);
  if (!isOpen) return null;
  return <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity" onClick={onClose} data-id="element-130" data-mp="39d9jxu6y7mh3" />
      {/* Bottom Sheet */}
      <div className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-3xl shadow-xl transform transition-transform max-h-[90vh] overflow-y-auto" data-id="element-131" data-mp="5ras6x72xg5z">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2" data-id="element-132" data-mp="1v7o93aukmffr">
          <div className="w-12 h-1 bg-gray-300 rounded-full" data-id="element-133" data-mp="1v86byqbqnrwn" />
        </div>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-134" data-mp="205pp1mwp9yn0">
          <h2 className="text-xl font-bold text-gray-900" data-id="element-135" data-mp="1lh1vfm6ezyzp">Add Funds</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-id="element-136" data-mp="1qfmnuzffdedl">
            <XIcon className="w-5 h-5 text-gray-500" data-id="element-137" data-mp="1cizqivjk8m75" />
          </button>
        </div>
        {/* Content */}
        <div className="px-6 py-6 space-y-6" data-id="element-138" data-mp="1hdn5o3soc492">
          {/* Amount Input */}
          <div data-id="element-139" data-mp="12kjxgn0z32v0">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-140" data-mp="12ktmo8e715zn">
              Enter Amount
            </label>
            <div className="relative" data-id="element-141" data-mp="2wx025je4e8xr">
              <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium" data-id="element-142" data-mp="2wy1myfkxu9so">
                SAR
              </span>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" className="w-full pl-16 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E2B55] text-lg font-medium" data-id="element-143" data-mp="q8hlabgk65wi" />
            </div>
          </div>
          {/* Quick Amount Buttons */}
          <div data-id="element-144" data-mp="18y7p1o2au20k">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-145" data-mp="18xxudn6b76oj">
              Quick Select
            </label>
            <div className="grid grid-cols-4 gap-2" data-id="element-146" data-mp="14x49z3s2sa0e">
              {quickAmounts.map(quickAmount => <button key={quickAmount} onClick={() => setAmount(quickAmount.toString())} className={`py-2 px-3 rounded-lg border-2 font-medium transition-all ${amount === quickAmount.toString() ? 'border-[#1E2B55] bg-blue-50 text-[#1E2B55]' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-147" data-mp="1iv2m02ixbucq">
                  {quickAmount}
                </button>)}
            </div>
          </div>
          {/* Payment Methods */}
          <div data-id="element-148" data-mp="3m7s5pp6dpl4d">
            <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-149" data-mp="3r4sbdyzbgluy">
              Payment Method
            </label>
            <div className="space-y-2" data-id="element-150" data-mp="3w4eng53erw26">
              {paymentMethods.map(method => <button key={method.id} onClick={() => setSelectedPaymentMethod(method.id)} className={`w-full p-4 rounded-lg border-2 transition-all flex items-center justify-between ${selectedPaymentMethod === method.id ? 'border-[#1E2B55] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-151" data-mp="2ygcut80jdvhi">
                  <div className="flex items-center gap-3" data-id="element-152" data-mp="3d6tzd08gx1im">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedPaymentMethod === method.id ? 'bg-[#1E2B55] text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-153" data-mp="2foty1ix3vdnx">
                      {method.icon}
                    </div>
                    <div className="text-left" data-id="element-154" data-mp="xtbecbn92gwq">
                      <p className="font-medium text-gray-900" data-id="element-155" data-mp="ssx4co238ww2">{method.name}</p>
                      <p className="text-xs text-gray-500" data-id="element-156" data-mp="sv02jvde9wbv">
                        {method.description}
                      </p>
                    </div>
                  </div>
                  {selectedPaymentMethod === method.id && <CheckCircleIcon className="w-6 h-6 text-[#1E2B55]" data-id="element-157" data-mp="e3herbeamxhy" />}
                </button>)}
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="px-6 py-6 border-t border-gray-200 bg-gray-50 sticky bottom-0" data-id="element-158" data-mp="53ulxdzmr00i">
          <button onClick={handleConfirm} disabled={!amount || !selectedPaymentMethod} className="w-full bg-[#1E2B55] text-white font-bold py-3 rounded-lg hover:bg-[#162041] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed" data-id="element-159" data-mp="3yh5cj3z4jw">
            Add SAR {amount || '0.00'}
          </button>
        </div>
      </div>
    </>;
}